<template>
  <AppDialog
    class="archive-offer-dialog essentials-offer-dialog"
    :class="{ accepted }"
    position="centered"
    :title="dialogTitle"
    show
    @close="closeDialog"
  >
    <div
      v-if="!accepted"
      class="offer-content"
      data-testid="essentials-offer-body"
    >
      <p class="subtitle">
        Instead of {{ actionGerund }}, switch to Essentials when your current annual plan ends on
        <strong>{{ formattedEffectiveOn }}</strong>. Nothing changes before then.
      </p>

      <div class="offer">
        <div class="plans">
          <div class="plan current-plan">
            <div class="plan-name">{{ offer.current_plan_title }}</div>
            <div class="plan-price">${{ formatPrice(offer.current_plan_price) }}</div>
            <div class="plan-price-period">/seat/mo</div>
          </div>
          <span class="arrow hi-thin-right-arrow"></span>
          <div class="plan essentials-plan">
            <div class="plan-name">Essentials</div>
            <div class="plan-price">${{ formatPrice(essentialsPlanPrice) }}</div>
            <div class="plan-price-period">/seat/mo</div>
          </div>
        </div>
      </div>

      <div class="keep-lose">
        <div class="keep-lose-column">
          <div class="keep-lose-title">Essentials includes</div>
          <div
            v-for="item in included"
            :key="item"
            class="keep-lose-row"
          >
            <i class="hi hi-check-circle-filled included-icon"></i>
            <span>{{ item }}</span>
          </div>
        </div>
        <div class="keep-lose-column">
          <div class="keep-lose-title">Essentials doesn't include</div>
          <div
            v-for="item in excluded"
            :key="item"
            class="keep-lose-row"
          >
            <i class="hi hi-remove excluded-icon"></i>
            <span>{{ item }}</span>
          </div>
        </div>
      </div>

      <p
        class="member-limit-note"
        data-testid="essentials-offer-member-limit-note"
      >
        <!-- TODO: unreachable while orgs above the Essentials limit are kept out of the offer; used again once that restriction is lifted. -->
        <template v-if="overLimit">
          Essentials is capped at {{ offer.essentials_member_limit }} members. You currently have
          {{ offer.active_members_count }}.
          {{ offer.over_limit_count }} {{ offer.over_limit_count === 1 ? 'member' : 'members' }}
          will move to view-only on {{ formattedEffectiveOn }} and will no longer track time.
        </template>
        <template v-else>
          Essentials is capped at {{ offer.essentials_member_limit }} members. You won't be able to
          add more without upgrading again.
        </template>
      </p>
    </div>

    <div
      v-else
      class="accepted-content"
      data-testid="essentials-offer-accepted"
    >
      <div class="accepted-illustration">
        <img :src="ESSENTIALS_ACCEPTED_ILLUSTRATION" />
      </div>
      <div class="accepted-title">Essentials starts when your plan ends</div>
      <p class="accepted-description">
        You're all set. Nothing changes until your current annual plan ends.
      </p>
    </div>

    <template #footer>
      <template v-if="!accepted">
        <a
          href="#"
          class="btn btn-default btn-default-hover-grey"
          data-testid="essentials-offer-decline"
          @click.prevent="next"
        >
          Continue to {{ actionNoun }}
        </a>
        <button
          class="btn btn-primary"
          :disabled="submitting"
          data-testid="essentials-offer-accept"
          @click="acceptOffer"
        >
          {{ submitting ? 'Switching…' : 'Switch to Essentials' }}
        </button>
      </template>
      <button
        v-else
        class="btn btn-primary"
        data-testid="essentials-offer-close"
        @click="closeDialog"
      >
        Close
      </button>
    </template>
  </AppDialog>
</template>

<script>
import AppDialog from '~/components/AppDialog.vue';
import { ESSENTIALS_ACCEPTED_ILLUSTRATION } from '~/rails_assets.js';
import { OrganizationsResource, AnalyticsResource } from '~/resources';

const INCLUDED = [
  'Time tracking',
  'Unlimited screenshots',
  'All your data so far',
];

const EXCLUDED = [
  'Timesheet approvals',
  'Payroll',
  'Integrations',
  'Add-ons such as Insights',
];

export default {
  name: 'EssentialsOfferDialog',
  components: {
    AppDialog,
  },
  props: {
    action: {
      type: String,
      default: 'archive',
    },
    offer: {
      type: Object,
      required: true,
    },
    // Same prop ArchiveOfferDialog reads, so both save steps quote Essentials from one source.
    essentialsPlanPrice: {
      type: [Number, null],
      default: null,
    },
    organizationId: {
      type: Number,
      required: true,
    },
    source: {
      type: String,
      default: null,
    },
  },
  emits: ['next', 'closeDialog'],
  data: () => ({
    ESSENTIALS_ACCEPTED_ILLUSTRATION,
    accepted: false,
    submitting: false,
  }),
  computed: {
    dialogTitle() {
      return this.accepted ? null : 'Switch to Essentials instead';
    },
    actionGerund() {
      return this.action === 'archive' ? 'archiving' : 'cancelling';
    },
    actionNoun() {
      return this.action === 'archive' ? 'archive' : 'cancel';
    },
    overLimit() {
      return this.offer.over_limit_count > 0;
    },
    included() {
      return [`1 simple flat fee covering ${this.offer.essentials_member_limit} users`, ...INCLUDED];
    },
    excluded() {
      return EXCLUDED;
    },
    formattedEffectiveOn() {
      if (!this.offer.effective_on) {return 'your renewal date';}

      // Parsed as UTC: the backend sends a plain date, and local parsing would shift it a day
      // back for anyone west of UTC.
      return new Date(`${this.offer.effective_on}T00:00:00Z`).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
      });
    },
  },
  created() {
    this.trackEvent('0228 Essentials Offer Viewed', {
      active_members_count: this.offer.active_members_count,
      over_limit_count: this.offer.over_limit_count,
    });
  },
  methods: {
    formatPrice(value) {
      return Number(value ?? 0).toFixed(2);
    },
    async acceptOffer() {
      if (this.submitting) {return;}

      this.submitting = true;

      try {
        const response = await OrganizationsResource.acceptEssentialsChurnOffer({ id: this.organizationId });

        if (response?.success) {
          this.accepted = true;
        } else {
          this.$appMessage(response?.error || 'Unable to switch to Essentials. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage(error.responseJSON?.error || 'Unable to switch to Essentials. Please try again.');
        }
      } finally {
        this.submitting = false;
      }
    },
    next(event) {
      this.$emit('next', event);
    },
    closeDialog() {
      this.$emit('closeDialog');
    },
    trackEvent(name, properties = {}) {
      AnalyticsResource.create(null, {
        event_name: name,
        event_properties: {
          organization_id: this.organizationId,
          action: this.action,
          ...(this.source && { source: this.source }),
          ...properties,
        },
      }).catch((error) => console.error(`Failed to track ${name}:`, error));
    },
  },
};
</script>
